import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { STAGE_LABELS } from '../lib/labels'
import { groupByStage, type StageWarnings } from '../lib/stageWarnings'
import type { DemandStage } from '../types/demand'

/**
 * The warning half of #988: what this demand was expected to have by now.
 *
 * This is NOT a gate. It replaces a dialog whose only button was "Close" —
 * the move it described was already refused by the time the user read it.
 * Here the move is still available: "Continue anyway" sends the acknowledgement
 * and proceeds, and what was skipped is recorded on the stage-history row.
 *
 * Violations are grouped by the stage that expected them, because a jump
 * settles several stages at once and a flat list of eleven fields tells the
 * user nothing about why they are being asked.
 */

const useStyles = makeStyles({
  lead: { marginBottom: tokens.spacingVerticalM },
  group: { marginBottom: tokens.spacingVerticalM },
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  groupTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: tokens.colorNeutralForeground3,
  },
  skippedTag: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '1px 6px',
    borderRadius: tokens.borderRadiusSmall,
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  list: { paddingLeft: '18px', margin: 0 },
  item: { marginBottom: tokens.spacingVerticalXS, fontSize: tokens.fontSizeBase300 },
  effects: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  effectsTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXS,
  },
})

interface Props {
  warnings: StageWarnings | null
  toStage: DemandStage | null
  busy?: boolean
  onContinue: (acknowledgeWith: string[]) => void
  onGoFix: () => void
  /** Override the auto-generated title. A resolve reads "Close as Promoted
   *  anyway?" better than the stage-derived "Move to Closed anyway?" (#1149). */
  title?: string
}

export function StageWarningDialog({ warnings, toStage, busy, onContinue, onGoFix, title }: Props) {
  const styles = useStyles()
  if (!warnings) return null

  const destination = toStage ? STAGE_LABELS[toStage] ?? toStage : 'the next stage'
  const groups = groupByStage(warnings.violations)

  // A confirmation, not a checklist: no violations to group, nothing to "fill
  // in". Returning a demand to New (#1142) is the case — the move is valid, the
  // user just confirms it. Keep the same acknowledge-and-continue plumbing.
  if (warnings.violations.length === 0) {
    return (
      <Dialog open modalType="alert" onOpenChange={(_, d) => !d.open && onGoFix()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{title ?? `Move to ${destination}?`}</DialogTitle>
            <DialogContent>
              <Text block>{warnings.message}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onGoFix} data-testid="warn-cancel">
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={busy}
                onClick={() => onContinue(warnings.acknowledgeWith)}
                data-testid="warn-continue"
              >
                Confirm
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    )
  }

  return (
    <Dialog open modalType="alert" onOpenChange={(_, d) => !d.open && onGoFix()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title ?? `Move to ${destination} anyway?`}</DialogTitle>
          <DialogContent>
            <Text block className={styles.lead}>
              {warnings.message}
            </Text>

            {groups.map((group) => (
              <div key={group.stage} className={styles.group} data-testid={`warn-group-${group.stage}`}>
                <div className={styles.groupHead}>
                  <span className={styles.groupTitle}>
                    {STAGE_LABELS[group.stage as DemandStage] ?? group.stage}
                  </span>
                  {group.skipped && <span className={styles.skippedTag}>skipped</span>}
                </div>
                <ul className={styles.list}>
                  {group.items.map((v) => (
                    <li key={v.code} className={styles.item} data-testid={`warn-${v.code}`}>
                      <strong>{v.label}</strong> — {v.message}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {warnings.sideEffects.length > 0 && (
              <div className={styles.effects} data-testid="warn-side-effects">
                <div className={styles.effectsTitle}>This move also…</div>
                <ul className={styles.list}>
                  {warnings.sideEffects.map((effect) => (
                    <li key={effect} className={styles.item}>
                      {effect}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onGoFix} data-testid="warn-go-fix">
              Go back and fill in
            </Button>
            <Button
              appearance="primary"
              disabled={busy}
              onClick={() => onContinue(warnings.acknowledgeWith)}
              data-testid="warn-continue"
            >
              Continue anyway
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
